import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";

import { CheckBoxField } from "@/components/Form/RHF/FormFields/CheckboxField/CheckBoxField.component";
import { RatingField } from "@/components/Form/RHF/FormFields/RatingField/ratingField.component";
import { TextAreaField } from "@/components/Form/RHF/FormFields/TextAreaField/textAreaField.component";
import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { ModalType } from "@/redux/modules/value/modal/modal.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { useModalRouter } from "@/utils/hooks/useModalRouter";

import {
    MAX_RATING,
    MIN_RATING,
    WriteInitialReviewFormValues,
    WriteProgressiveReviewFormValues
} from "../writeReview.schema";

type ReviewPartialProps = { authorDetails: boolean; disabled?: boolean };

const ReviewPartial = ({ authorDetails, disabled }: ReviewPartialProps) => {
    const { t } = useGranite();

    const { open: openGuidanceModal } = useModalRouter(
        ModalType.ReviewGuidance
    );

    const { open: openTermsAndConditionsModal } = useModalRouter(
        ModalType.ReviewTermsAndConditions
    );

    const handleOnTermsAndConditionsClick = () => {
        openTermsAndConditionsModal();
    };

    const handleOnGuidanceClick = () => {
        openGuidanceModal();
    };

    return (
        <>
            <Row>
                <Col>
                    <RatingField<WriteProgressiveReviewFormValues>
                        name="rating"
                        required={true}
                        label={t("product.reviews.form.rating.label")}
                        disabled={disabled}
                        min={MIN_RATING}
                        max={MAX_RATING}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <TextField<WriteProgressiveReviewFormValues>
                        name={"title"}
                        label={t("product.reviews.form.title.label")}
                        required={false}
                        placeholder={t(
                            "product.reviews.form.title.placeholder"
                        )}
                        disabled={disabled}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <TextAreaField<WriteProgressiveReviewFormValues>
                        name="text"
                        label={t("product.reviews.form.text.label")}
                        required={false}
                        placeholder={t("product.reviews.form.text.placeholder")}
                        disabled={disabled}
                        minLength={50}
                        optionalIndication={true}
                        microCopy={() => (
                            <Button
                                color={"link"}
                                className={cn(
                                    "tw:absolute tw:right-0 tw:top-0"
                                )}
                                size={"sm"}
                                onClick={handleOnGuidanceClick}
                            >
                                {t("product.reviews.form.reviewtext.guidance")}
                            </Button>
                        )}
                    />
                </Col>
            </Row>
            {authorDetails && (
                <>
                    <Row>
                        <Col>
                            <TextField<WriteInitialReviewFormValues>
                                name={"nickname"}
                                label={t("product.reviews.form.nickname.label")}
                                required={true}
                                placeholder={t(
                                    "product.reviews.form.nickname.placeholder"
                                )}
                                disabled={disabled}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextField<WriteInitialReviewFormValues>
                                name={"email"}
                                label={t("product.reviews.form.email.label")}
                                required={true}
                                placeholder={t(
                                    "product.reviews.form.email.placeholder"
                                )}
                                disabled={disabled}
                            />
                        </Col>
                    </Row>
                </>
            )}
            <Row>
                <Col className={"d-flex align-items-center"}>
                    <CheckBoxField<WriteProgressiveReviewFormValues>
                        name={"agreedToTermsAndConditions"}
                        required={true}
                        disabled={disabled}
                        label={t(
                            "product.reviews.form.agreedToTermsAndConditions.label"
                        )}
                    >
                        {() => (
                            <>
                                {t(
                                    "product.reviews.form.agreedToTermsAndConditions.label"
                                )}
                                <Button
                                    color={"link"}
                                    className={"p-0 ml-1"}
                                    onClick={handleOnTermsAndConditionsClick}
                                >
                                    {t(
                                        "product.reviews.form.agreedToTermsAndConditions.document"
                                    )}
                                </Button>
                            </>
                        )}
                    </CheckBoxField>
                </Col>
            </Row>
        </>
    );
};

export { ReviewPartial };
